/**
 * NeuroNeeti data repository — the public data-access surface of the app.
 *
 * All UI components must access domain data through this module.
 * Never import from `@/data/workbook.json`, never import the adapter
 * directly. Swapping the backing store to a real API is a matter of
 * changing which adapter `@/data/adapters` re-exports.
 *
 * All returned objects are treated as immutable snapshots — the UI
 * must not mutate them.  All persistent IDs are preserved verbatim so
 * every provenance link remains traceable.
 */
import { activeAdapter as A } from "@/data/adapters";

/* ------------------------ people & surgeons ------------------------ */
export const getPerson              = (id) => A.byId("personById", id);
export const getAllPeople           = () => A.collection("people");
export const getSurgeonProfile      = (personId) => {
  const person = A.byId("personById", personId);
  const profile = A.byId("surgeonProfileById", personId);
  if (!person && !profile) return null;
  return { ...(person || {}), ...(profile || {}), personId };
};
export const getAllSurgeons         = () =>
  A.collection("surgeonProfiles").map((p) => getSurgeonProfile(p.personId));

/* --------------------------- departments --------------------------- */
export const getDepartments         = () => A.collection("departments");

/* ------------------------------ cases ------------------------------ */
export const getCases               = (filters = {}) => {
  let rows = A.collection("cases");
  if (filters.surgeonId) {
    // participants OR primary attending
    const participantCaseIds = new Set(
      A.collection("caseParticipants")
        .filter((p) => p.personId === filters.surgeonId)
        .map((p) => p.caseId)
    );
    rows = rows.filter(
      (c) =>
        c.primaryAttendingId === filters.surgeonId ||
        c.primaryResidentId === filters.surgeonId ||
        participantCaseIds.has(c.caseId)
    );
  }
  if (filters.diagnosis) {
    rows = rows.filter((c) => c.diagnosis === filters.diagnosis);
  }
  if (filters.status) {
    rows = rows.filter((c) => c.currentStatus === filters.status);
  }
  if (filters.koos) {
    rows = rows.filter((c) => c.koosGrade === filters.koos);
  }
  return rows;
};
export const getCaseById            = (caseId) => A.byId("caseById", caseId);
export const getCasesBySurgeon      = (personId) => getCases({ surgeonId: personId });
export const getCaseParticipants    = (caseId) =>
  A.collection("caseParticipants").filter((p) => p.caseId === caseId);
export const getSurgicalDetailByCaseId = (caseId) =>
  A.collection("surgicalDetails").find((s) => s.caseId === caseId) || null;

/* -------------------------- case sub-tables ------------------------- */
export const getEncountersByCaseId  = (caseId) =>
  A.collection("encounters")
    .filter((e) => e.caseId === caseId)
    .sort((a, b) => String(a.encounterDate).localeCompare(String(b.encounterDate)));

export const getTimelineByCaseId    = (caseId) =>
  A.collection("timelineEvents")
    .filter((t) => t.caseId === caseId)
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

export const getReflectionsByCaseId = (caseId) =>
  A.collection("reflections").filter((r) => r.caseId === caseId);

export const getOperativePearlsByCaseId = (caseId) =>
  A.collection("operativePearls").filter((p) => p.caseId === caseId);

export const getImagingByCaseId     = (caseId) =>
  A.collection("imaging").filter((i) => i.caseId === caseId);

export const getMMByCaseId          = (caseId) =>
  A.collection("mmRecords").filter((m) => m.caseId === caseId);

export const getCollaborationByCaseId = (caseId) =>
  A.collection("collaboration").filter((c) => c.contextId === caseId);

/**
 * Generic context-scoped collaboration lookup. `contextType` matches
 * the workbook's Collaboration.contextType field (Case, Imaging,
 * Reflection, M&M, Pre-Case Review). `contextId` is the persistent ID
 * of that record. Returns workbook-seeded records only — demo-layer
 * records live in Ni2DemoStore and must be merged by the caller when
 * needed.
 */
export const getCollaborationByContext = (contextType, contextId) =>
  A.collection("collaboration").filter(
    (c) => c.contextType === contextType && c.contextId === contextId
  );

/**
 * People search for @mention menu. Case-insensitive, substring match
 * against fullName + role + subspecialty so a partial like "ra" or
 * "skull" surfaces the right people. Returns a shallow copy sorted
 * with attendings first, then residents, then everyone else.
 */
export const getPeopleForMentionSearch = (query = "", limit = 8) => {
  const q = String(query || "").trim().toLowerCase();
  const people = A.collection("people");
  const filtered = q
    ? people.filter((p) =>
        [p.fullName, p.role, p.subspecialty, p.primaryClinicalFocus, p.personId]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q))
      )
    : people;
  const rank = (p) =>
    /attending/i.test(p.role) ? 0 : /resident/i.test(p.role) ? 1 : 2;
  return [...filtered]
    .sort((a, b) => rank(a) - rank(b) || String(a.fullName).localeCompare(b.fullName))
    .slice(0, limit);
};

/**
 * Case files — the union of every workbook-derived file record for
 * this case. Imaging is the only real file type in the workbook; the
 * returned list is normalized into a single File shape so the browser
 * component can render Imaging, demo uploads and future file types
 * uniformly. Every returned record carries `source` = "imaging" |
 * "demo" so the UI can badge session-only records correctly.
 *
 * Demo uploads created via Ni2DemoStore are NOT merged in here — the
 * caller merges them, so this function stays a pure read of the
 * immutable workbook seed.
 */
export const getFilesByCaseId = (caseId) => {
  const imaging = A.collection("imaging").filter((i) => i.caseId === caseId);
  return imaging.map((i) => ({
    fileId: i.imagingId,
    imagingId: i.imagingId,
    caseId: i.caseId,
    fileName: i.filenamePlaceholder,
    fileType: "Imaging",
    modality: i.modality,
    sequence: i.sequence,
    studyDate: i.studyDate,
    studyType: i.studyType,
    label: `${i.modality} · ${i.sequence}`,
    keyFinding: i.keyFinding,
    source: "imaging",
    demo: false,
  }));
};

/* --------------------------- single-record --------------------------- */
export const getReflection          = (id) => A.byId("reflectionById", id);
export const getOperativePearl      = (id) => A.byId("operativePearlById", id);
export const getImaging             = (id) => A.byId("imagingById", id);
export const getMMRecord            = (id) => A.byId("mmRecordById", id);
export const getEncounter           = (id) => A.byId("encounterById", id);
export const getTimelineEvent       = (id) => A.byId("timelineEventById", id);
export const getCollaboration       = (id) => A.byId("collaborationById", id);

/* ---------------------------- literature ---------------------------- */
/**
 * Deterministic demo identity + ownership overlay.
 * Kept in sync with `backend/workbook_tools.py`. The workbook JSON
 * stays immutable — ownership is applied at read time.
 */
export const CURRENT_USER = { userId: "dr_rao", displayName: "Dr. Maya Rao" };

const OWNERSHIP_OVERLAY = {
  cases: {
    "VS-RAO-001": { primarySurgeonId: "dr_rao", ownerUserId: "dr_rao", recordScope: "personal" },
  },
  reflections: {
    "REF-001": { authorUserId: "dr_rao", ownerUserId: "dr_rao", recordScope: "personal" },
    "REF-002": { authorUserId: "dr_rao", ownerUserId: "dr_rao", recordScope: "personal" },
    "REF-003": { authorUserId: "dr_rao", ownerUserId: "dr_rao", recordScope: "personal" },
    "REF-050": { authorUserId: "dr_rao", ownerUserId: "dr_rao", recordScope: "personal" },
  },
  operativePearls: {
    "PEARL-001": { authorUserId: "dr_rao", ownerUserId: "dr_rao", recordScope: "personal" },
    "PEARL-002": { authorUserId: "dr_rao", ownerUserId: "dr_rao", recordScope: "personal" },
    "PEARL-003": { authorUserId: "dr_rao", ownerUserId: "dr_rao", recordScope: "personal" },
  },
  imaging: {
    "IMG-001": { ownerUserId: "dr_rao", recordScope: "personal" },
    "IMG-003": { ownerUserId: "dr_rao", recordScope: "personal" },
    "IMG-004": { ownerUserId: "dr_rao", recordScope: "personal" },
  },
};

const OWNERSHIP_KEY = { cases: "caseId", reflections: "reflectionId", operativePearls: "operativePearlId", imaging: "imagingId" };

const _annotate = (collection, record) => {
  if (!record) return record;
  const map = OWNERSHIP_OVERLAY[collection] || {};
  const idField = OWNERSHIP_KEY[collection];
  const overlay = map[record?.[idField]] || {};
  return {
    ...record,
    recordScope: record.recordScope || overlay.recordScope || "department",
    ...overlay,
  };
};

export const getCurrentUser = () => ({ ...CURRENT_USER });

export const isPersonalRecord = (collection, record) =>
  _annotate(collection, record)?.recordScope === "personal";

export const getPersonalCases = (userId = CURRENT_USER.userId) =>
  A.collection("cases").map((c) => _annotate("cases", c)).filter((c) => c.ownerUserId === userId);

export const getPersonalReflections = (userId = CURRENT_USER.userId) =>
  A.collection("reflections").map((r) => _annotate("reflections", r)).filter((r) => r.authorUserId === userId);

export const getPersonalOperativePearls = (userId = CURRENT_USER.userId) =>
  A.collection("operativePearls").map((p) => _annotate("operativePearls", p)).filter((p) => p.authorUserId === userId);

export const getPersonalImaging = (userId = CURRENT_USER.userId) =>
  A.collection("imaging").map((i) => _annotate("imaging", i)).filter((i) => i.ownerUserId === userId);

export const getLiterature          = () => A.collection("literature");

export const getLiteratureById      = (id) => A.byId("literatureById", id);
export const getLiteratureByIds     = (ids = []) => ids.map((id) => A.byId("literatureById", id)).filter(Boolean);
export const getMMRecordById        = (id) => A.byId("mmRecordById", id);
export const getAllMMRecords        = () => A.collection("mmRecords");
export const getPeopleByIds         = (ids = []) => ids.map((id) => A.byId("personById", id)).filter(Boolean);

/**
 * Unified departmental memory search. Merges the immutable workbook
 * M&M seed data with locally published demo M&Ms (passed in by the
 * MM store). Returns matches ranked by keyword density across
 * complication, learning points, action items, chronology,
 * teaching points, and change-in-practice.
 *
 * Deterministic; production would replace this with a
 * retrieval-augmented call.
 */
export const searchDepartmentMemory = (query, { publishedMMs = [] } = {}) => {
  const q = String(query || "").trim().toLowerCase();
  const workbook = A.collection("mmRecords");
  const rankedWorkbook = workbook.map((m) => {
    const hay = [
      m.complication, m.chronology, m.keyDecisionPoint, m.contributingFactors,
      m.protectiveFactors, m.learningPoints, m.actionItems, m.discussionQuestions,
    ].filter(Boolean).join(" ").toLowerCase();
    const score = q ? q.split(/\s+/).filter((w) => w.length > 2).reduce((s, w) => (hay.includes(w) ? s + 1 : s), 0) : 1;
    return { kind: "workbook", record: m, score };
  });
  const rankedDemo = publishedMMs.map((p) => {
    const s = p.synthesis || {};
    const hay = [
      s.rootCause?.text, s.chronology?.text, s.keyDecisionPoint?.text, s.changeInPractice?.text,
      ...(s.teachingPoints || []).map((t) => t.text),
      ...(s.actionItems || []).map((t) => t.text),
      ...(s.contributingFactors || []).map((t) => t.text),
      ...(s.protectiveFactors || []).map((t) => t.text),
    ].filter(Boolean).join(" ").toLowerCase();
    const score = q ? q.split(/\s+/).filter((w) => w.length > 2).reduce((sc, w) => (hay.includes(w) ? sc + 1 : sc), 0) + 2 /* demo boost */ : 2;
    return { kind: "demo", record: p, score };
  });
  return [...rankedDemo, ...rankedWorkbook]
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score);
};

/* ------------------------- derived insights ------------------------- */
export const getDerivedInsights     = (filters = {}) => {
  let rows = A.collection("derivedInsights");
  if (filters.scope) rows = rows.filter((r) => r.scope === filters.scope);
  if (filters.personId) rows = rows.filter((r) => r.personId === filters.personId);
  if (filters.departmentId) rows = rows.filter((r) => r.departmentId === filters.departmentId);
  if (filters.topic) rows = rows.filter((r) => r.topic === filters.topic);
  return rows;
};
export const getDerivedInsightById  = (id) => A.byId("derivedInsightById", id);

/* ------------------------ suggested questions ------------------------ */
export const getSuggestedQuestions  = (filters = {}) => {
  let rows = A.collection("suggestedQuestions");
  if (filters.sceneNumber != null)
    rows = rows.filter((q) => q.sceneNumber === filters.sceneNumber);
  return rows;
};
export const getSuggestedQuestionById = (id) =>
  A.byId("suggestedQuestionById", id);

/* ------------------------- composite views ------------------------- */
/**
 * Everything a pre-case review card needs — the case + its encounters,
 * timeline, reflections, imaging, M&M and referenced literature.
 */
export const getPreCaseReviewData   = (caseId) => {
  const c = getCaseById(caseId);
  if (!c) return null;
  const mms = getMMByCaseId(caseId);
  const litIds = new Set(mms.flatMap((m) => m.literatureIds || []));
  return {
    case: c,
    surgicalDetail: getSurgicalDetailByCaseId(caseId),
    participants:  getCaseParticipants(caseId),
    encounters:    getEncountersByCaseId(caseId),
    timeline:      getTimelineByCaseId(caseId),
    reflections:   getReflectionsByCaseId(caseId),
    operativePearls: getOperativePearlsByCaseId(caseId),
    imaging:       getImagingByCaseId(caseId),
    mmRecords:     mms,
    literature:    getLiterature().filter((l) => litIds.has(l.literatureId)),
    collaboration: getCollaborationByCaseId(caseId),
  };
};

/**
 * Department-scope roll-up used by the dashboard / insights views.
 */
export const getDepartmentInsights  = (filters = {}) => {
  const departmentId = filters.departmentId;
  const cases = departmentId
    ? A.collection("cases").filter((c) => c.departmentId === departmentId)
    : A.collection("cases");
  const insights = departmentId
    ? getDerivedInsights({ departmentId, scope: "Departmental" })
    : getDerivedInsights({ scope: "Departmental" });
  return { cases, insights };
};

/* --------------------- provenance-chip helpers --------------------- */
/**
 * Resolve an arbitrary provenance reference into its underlying record.
 * The Ni² Workspace passes provenance chips like
 * { label: "Case", ref: "VS-RAO-001" } — this helper returns the
 * matching record regardless of which sheet it lives in, so the UI
 * doesn't need per-kind branching.
 */
export const resolveProvenance      = (label, ref) => {
  if (!ref) return null;
  const l = String(label || "").toLowerCase();
  if (l === "case")       return getCaseById(ref);
  if (l === "reflection") return getReflection(ref);
  if (l === "imaging")    return getImaging(ref);
  if (l === "paper")      return getLiteratureById(ref);
  if (l === "m&m" || l === "mm") return getMMRecord(ref);
  // Fallback — probe every index
  return (
    getCaseById(ref) ||
    getReflection(ref) ||
    getImaging(ref) ||
    getLiteratureById(ref) ||
    getMMRecord(ref) ||
    null
  );
};

/* --------------------------- meta / debug --------------------------- */
export const getDatasetMeta         = () => A.meta();
